<script setup>
import { onMounted, reactive, ref, computed } from "vue";
import { appBarStore } from "@/stores/appBar";
import { mdmServerStore } from "@/stores/mdmServer";
import { apiQuery } from "@/api/j24api";
import { fetchAuthSession } from "aws-amplify/auth";
import VueDatePicker from "@vuepic/vue-datepicker";
import AlertsList from "@/components/AlertsList.vue";

const appBar = appBarStore();
const mdmServer = mdmServerStore();

const dateFormat = "dd/MM/yyyy, HH:mm"; // Date format for the date picker.
const statuses = [
  "NotStarted",
  "Unknown",
  "PlanCompleted",
  "PlanFailed",
  "PlanCanceled",
  "N/A",
  "New",
]; // Don't send cancel requests for these statuses.

const readOnlyStates = ["active", "cancelled", "done"]; // If the object status is in this list, mark the field as read-only.

var mdmType = null;
var planObject = null; // Main plan object from the MongoDB cache.
var objectsToCancel = []; // Plans selected for cancellation.
var allGroups = []; // All groups available to select for master group.

const loading = ref(false); // Get data loading dialog.
const updating = ref(false); // Update changes loading dialog.
const showAddList = ref(false); // Add group selection dialog.
const showRemoveList = ref(false); // Remove group selection dialog.

var updateGroups = reactive([]); // Live objects in the table.
var newGroups = reactive([]); // Filtered (minus already active) update groups avaiable to select.
var iOSVersions = reactive([]); // Available iOS versions.
var macOSVersions = reactive([]); // Available macOS versions.
var alerts = reactive([]); // Alert messages.

var addGroupSearch = ref(""); // Search term for the add group dialog.

var selectedGroup = ref(null);
var selectedRemoveGroup = ref(null);

var cancelDialogBox = ref(false);
var cancelDialogBoxMessage = ref("");

const headers = [
  {
    text: "Update Group",
    value: "name",
  },
  {
    text: "Group ID",
    value: "id",
  },
  {
    text: "Platform",
    value: "type",
  },
  {
    text: "Target Version",
    value: "targetVersion",
  },
  {
    text: "Update Start",
    value: "start",
  },
  {
    text: "Update End (Optional)",
    value: "end",
  },
  {
    text: "Plan Status",
    value: "planStatus",
  },
  {
    text: "Devices Pending",
    value: "devicesPending",
  },
  {
    text: "Cancel Plan",
    value: "cancelPlan",
  },
];

appBar.$subscribe((mutation, state) => {
  console.log("Change Triggered");
  if (appBar.selected != "") {
    getData();
  }
});

onMounted(() => {
  if (appBar.selected != "") {
    console.log("Mounted - Getting data");
    getData();
  }
});

async function resetPage() {
  planObject = null;
  objectsToCancel.length = 0;
  allGroups.length = 0;
  newGroups.length = 0;
  updateGroups.length = 0;
  updating.value = false;
  loading.value = false;
}

async function getData() {
  let updateObj = null;

  await resetPage();

  loading.value = true;

  try {
    mdmType = mdmServer.$state.type;

    updateObj = await apiQuery("ELA", "updateAutomation/getPlanData", {
      command: "getData",
      mdm: mdmType,
      mdmServerId: appBar.selected,
    });

    // Grab the available iOS/macOS versions. Remove all but the latest 8.
    iOSVersions = (updateObj?.availableUpdates?.iOS || []).slice(0, 8);
    macOSVersions = (updateObj?.availableUpdates?.macOS || []).slice(0, 8);

    // Populate planObject.
    planObject = updateObj?.updateConfig || null;

    // If there is a plan object and set update groups, get the latest data.
    if (planObject && planObject.updateGroups.length > 0) {
      const statuses = await apiQuery("ELA", "updateAutomation/getPlanData", {
        command: "getPlanStatus",
        mdm: mdmType,
        mdmServerId: appBar.selected,
      });

      planObject.updateGroups.forEach((group) => {
        const jamfGroup = statuses.find((j) => j.groupId === group.id);
        if (jamfGroup) {
          updateGroups.push({
            ...group,
            planStatus: jamfGroup.planStatus,
            devicesPending: jamfGroup.devicesPending,
            errorReasons: jamfGroup.errorReasons,
          });
        } else {
          // If the plan dissappears, preserve the outcome if the DB object is complete.
          let state = "NotStarted";
          if (group.status === "done") {
            state = "PlanCompleted";
          }

          if (group.status === "cancelled") {
            state = "PlanCanceled";
          }

          updateGroups.push({
            ...group,
            planStatus: state,
            devicesPending: "N/A",
            errorReasons: [],
          });
        }
      });
    }
  } catch (error) {
    console.log(error);
    alert("error", "Error loading data.");
  }

  loading.value = false;
}

function alert(type, message, timeout) {
  alerts.push({
    type: type,
    message: message,
    closable: true,
    timeout: timeout,
  });
}

async function cancelPlans(type, item) {
  let cancelList = [];

  switch (type) {
    case "All":
      if (updateGroups.length === 0) {
        alert("info", "There are no plans to cancel.", 7);
        return;
      }

      objectsToCancel = updateGroups;
      cancelDialogBoxMessage.value =
        "Are you sure you want to cancel all in-progress plans?";
      cancelDialogBox.value = true;
      break;
    case "Single":
      objectsToCancel.push(item);
      cancelDialogBoxMessage.value =
        "Are you sure you want to cancel this plan?";
      cancelDialogBox.value = true;
      break;
    case "Cancel":
      objectsToCancel = [];
      cancelDialogBox.value = false;
      break;
    case "API":
      cancelDialogBox.value = false;
      updating.value = true;

      try {
        // Filter out objects that are already completed.
        cancelList = objectsToCancel.filter(
          (item) => !statuses.includes(item.planStatus)
        );

        // If there are objects to cancel, send the request.
        if (cancelList.length > 0) {
          await apiQuery("ELA", "updateAutomation/updatePlanData", {
            mdm: mdmType,
            command: "cancelPlans",
            mdmServerId: appBar.selected,
            cancelPlans: [...cancelList],
            lastEditedBy: await getPortalUser(),
          });
        } else {
          if (item === "Remove") {
            // If we're removing all the groups, reset updateGroups and return.

            // Copy the removed groups back into the newGroups array for re-selection.
            for (let group of updateGroups) {
              newGroups.push({
                id: group.id,
                name: group.name,
                type: group.type,
              });
            }

            alert(
              "success",
              "Plans removed (and cancelled if applicable) successfully.",
              5
            );

            updateGroups.length = 0;
            updating.value = false;
            return;
          }

          alert("info", "No applicable plans to cancel.", 5);
          objectsToCancel = [];
          updating.value = false;
          return;
        }

        alert("success", "Cancellation successful.", 5);
      } catch (error) {
        console.log(error);
        alert("error", "Cancellation failed.");
      }

      await getData(); // Refresh the data after cancelling plans.
      updating.value = false;
      break;
  }
}

async function getPortalUser() {
  let email = "Unknown";
  // Get the ELA authenticated user's email address.
  if (process.env.NODE_ENV === "development") {
    email = "testuser@developmentenv.com";
  } else {
    const currentAuthenticatedUser = await fetchAuthSession();
    if (currentAuthenticatedUser) {
      email = currentAuthenticatedUser.tokens.idToken.payload.email;
    }
  }

  return { user: email, timestamp: new Date().toISOString() };
}

function updateTargetVersion(item, value, scope) {
  if (scope === "All") {
    updateGroups.forEach((group) => {
      group.targetVersion = value;
    });
  } else {
    const group = updateGroups.find((group) => group.id === item.id);
    if (group) {
      group.targetVersion = value;
    }
  }
}

async function preSaveValidation() {
  /*
    Should a plan be executed in the backgroud before saving changes, we need to grab the status and planUuid before saving.
    This is to prevent an active plan from being overwritten.
    We perform a quick grab of the db status for each object and add their live details to the updateGroups item.
    This assures that the planUuid and status are not lost during the save process.
  */

  let obj = await apiQuery("ELA", "updateAutomation/getPlanData", {
    command: "getData",
    mdm: mdmType,
    mdmServerId: appBar.selected,
  });

  let plans = obj?.updateConfig?.updateGroups || [];

  plans.forEach((plan) => {
    const group = updateGroups.find(
      (updateGroup) => updateGroup.id === plan.id
    );
    if (group) {
      /* If the plan is New, we don't need to update the planUuid.
      This is to prevent a cancelled plan re-populating the resetted groups status, for example. */
      if (group.planStatus !== "New") {
        group.planUuid = plan.planUuid;
        group.status = plan.status;
      }
    }
  });
}

function hasDuplicateIds(arr) {
  // Quick search to see if there are any duplicate IDs in the array.
  const seenIds = new Set();
  for (const obj of arr) {
    if (seenIds.has(obj.id)) {
      return true; // Duplicate found
    }
    seenIds.add(obj.id);
  }
  return false; // No duplicates
}

async function saveChanges() {
  if (hasDuplicateIds(updateGroups)) {
    alert(
      "error",
      "There are duplicate groups detected, please reload the page.",
      7
    );
    return;
  }

  for (let group of updateGroups) {
    if (!group.targetVersion) {
      alert("info", "All plans must have a target version.", 7);
      return;
    }
  }

  for (let group of updateGroups) {
    if (!group.start) {
      alert("info", "All plans must have a start date.", 7);
      return;
    }
  }

  if (mdmType !== "Jamf") {
    alert("warning", "Selected MDM is not Jamf, please choose another MDM.", 7);
    return;
  }

  updating.value = true;

  await preSaveValidation(); // Grab the planUuid and status of existing plans before saving.

  try {
    await apiQuery("ELA", "updateAutomation/updatePlanData", {
      mdm: mdmType,
      command: "update",
      mdmServerId: appBar.selected,
      updateGroups: updateGroups,
      lastEditedBy: await getPortalUser(),
    });

    await getData(); // Refresh the data after saving changes.

    alert("success", "Changes saved successfully.", 5);
  } catch (error) {
    console.log(error);
    alert("error", "Error saving changes.");
  }

  updating.value = false;
}

async function setDatePicker(item, type) {
  // Helper function to parse date strings in "dd/MM/yyyy, HH:mm" format to Date objects
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    // Expected format: "dd/MM/yyyy, HH:mm"
    const parts = dateStr.split(", ");
    const dateParts = parts[0].split("/");
    const timeParts = parts[1].split(":");
    // Date constructor expects: year, month (0-indexed), day, hour, minute
    return new Date(
      dateParts[2],
      dateParts[1] - 1,
      dateParts[0],
      timeParts[0],
      timeParts[1]
    );
  };

  // Grab the item from the updateGroups array for checking.
  let t = updateGroups.find((y) => y.id === item.id);

  // If we're only clearing the field, do nothing.
  if (!item.end && type === "End") {
    t.end = item.end; // Update end to be null.
    return;
  }

  // If we're only clearing the field, do nothing.
  if (!item.start && type === "Start") {
    // Unless - There is still a date in the end column.
    if (item.end) {
      alert("info", "You must have a start date when setting an end date.", 7);
      return;
    }
    t.start = item.start; // Update start to be null.
    return;
  }

  // If we reach here, we are updating the date, not clearing it.
  if (t) {
    switch (type) {
      case "Start": {
        // If the start date is in the past, refuse.
        const startDate = parseDate(item.start);
        // Compare at minute precision (ignore seconds/milliseconds)
        const now = new Date();
        now.setSeconds(0, 0);
        const startDateMinute = new Date(startDate);
        startDateMinute.setSeconds(0, 0);

        if (startDateMinute < now) {
          alert("info", "The start date cannot be in the past.", 7);
          return;
        }

        // If the start date is older than the end date, refuse.
        if (t.end) {
          const endDate = parseDate(t.end);
          if (startDate >= endDate) {
            alert(
              "info",
              "The start date cannot be later or equal to the end date.",
              7
            );
            return;
          }
        }
        t.start = item.start; // Update the start date in the table.
        break;
      }

      case "End": {
        // If there is an end date but not a start date, refuse.
        if (!t.start) {
          alert("info", "You must have a start date to set an end date.", 7);
          return;
        }

        // If the end date is earlier than the start date, refuse.
        const endDate = parseDate(item.end);
        const existingStartDate = parseDate(t.start);
        if (endDate <= existingStartDate) {
          console.log("End date check", item.end, t.start);
          alert(
            "info",
            "The end date cannot be earlier or equal to the start date.",
            7
          );
          return;
        }

        t.end = item.end; // Update the end date in the table.
        break;
      }
    }
  }
}

async function loadSmartGroups() {
  let groups = [];

  try {
    if (newGroups.length === 0) {
      loading.value = true;
      groups = await apiQuery("ELA", "updateAutomation/getPlanData", {
        mdm: mdmType,
        command: "getSmartGroups",
        mdmServerId: appBar.selected,
      });

      loading.value = false;

      if (groups.length === 0) {
        alert("info", "There are no groups available to add.", 7);
        return;
      }

      // Filter out groups that are already in the updateGroups array.
      newGroups = groups.filter(
        (group) =>
          !updateGroups.some(
            (updateGroup) => group.id === parseInt(updateGroup.id)
          )
      );
    }
  } catch (error) {
    console.log(error);
    alert("error", "Error loading groups.");

    loading.value = false;
    return;
  }
}

async function addGroup() {
  addGroupSearch.value = ""; // Reset the search term.

  await loadSmartGroups();

  showAddList.value = true;
}

async function removeGroup(type, item) {
  let index = null;

  await loadSmartGroups();

  switch (type) {
    case "All":
      objectsToCancel = updateGroups; // Prep the objects to cancel.
      await cancelPlans("API", "Remove"); // Cancel/remove all plans.
      showRemoveList.value = false;
      break;
    case "Single":
      // If no group is selected, close the dialog box without crashing.
      if (!item) {
        showRemoveList.value = false;
        return;
      }
      // Remove from table.
      index = updateGroups.findIndex((group) => group.id === item.id);
      if (index !== -1) {
        updateGroups.splice(index, 1);
      }

      // Cancel the plan if it's not already completed.
      if (!statuses.includes(item.planStatus)) {
        await cancelPlans("Single", item);
      }

      // Add to selection group.
      newGroups.push({
        id: item.id,
        name: item.name,
        type: item.type,
      });

      showRemoveList.value = false;
      break;
    default:
      showRemoveList.value = true;
      break;
  }
}

async function addSelectedGroup(item) {
  // Check if a group is selected.
  if (!item) {
    showAddList.value = false;
    alert("info", "You must select a group to add.", 7);
    return;
  }

  // Add to the updateGroups array/table.
  updateGroups.push({
    name: item.name,
    id: item.id,
    type: item.type || "Unknown",
    start: "",
    end: "",
    planStatus: "New",
    devicesPending: "N/A",
    status: "pending",
    errorReasons: [],
  });

  // Remove from selection group.
  const index = newGroups.findIndex((group) => group.id === item.id);

  if (index !== -1) {
    newGroups.splice(index, 1);
  }

  showAddList.value = false;
}

async function testPlan() {
  // This functions starts the plan process for testing purposes, skipping the schedule.
  const statuses = await apiQuery(
    "ELA",
    "updateAutomation/processPlansStart",
    {}
  );
  await getData();
}

async function updatePlanStatuses() {
  if (updateGroups.length === 0) {
    alert("info", "There are no plans to update.", 7);
    return;
  }

  loading.value = true;

  try {
    const statuses = await apiQuery("ELA", "updateAutomation/getPlanData", {
      mdm: mdmType,
      command: "getPlanStatus",
      mdmServerId: appBar.selected,
    });

    // Update the table with the new statuses, matching by group ID.
    updateGroups.forEach((group) => {
      const t = statuses.find((j) => j.groupId === group.id);
      if (t) {
        group.planStatus = t.planStatus;
        group.devicesPending = t.devicesPending;
        group.state = t.state;
        group.errorReasons = t.errorReasons;
      } else {
        group.planStatus = "NotStarted";
        group.devicesPending = "N/A";
      }
    });
  } catch (error) {
    console.log(error);
    alert("error", "Error updating plan statuses.");
  }

  loading.value = false;
}
</script>

<template>
  <template v-if="!appBar.selected || mdmServer.$state.type !== 'Jamf'">
    <v-col cols="12">
      <h3>Select a Jamf MDM Server</h3>
    </v-col>
  </template>
  <v-row v-if="alerts">
    <v-col>
      <AlertsList :modelValue="alerts" />
    </v-col>
  </v-row>
  <v-col>
    <v-row v-if="appBar.selected && mdmServer.$state.type === 'Jamf'">
      <v-col cols="auto" class="ml-auto">
        <!-- Uncomment this to show the button to skip the schedule -->
        <!-- <v-btn class="mr-4" color="primary" @click="testPlan()">Test</v-btn> -->
        <v-btn
          v-if="updateGroups.length > 0"
          color="primary"
          @click="updatePlanStatuses()"
          >Refresh Statuses</v-btn
        >
      </v-col>
    </v-row>
    <v-spacer class="mb-4"></v-spacer>

    <EasyDataTable
      v-if="appBar.selected && mdmServer.$state.type === 'Jamf'"
      :headers="headers"
      :items="updateGroups"
      alternating
      sort-by="name"
      must-sort
      theme-color="#e7541e"
    >
      <template #empty-message>No Groups Selected</template>
      <template #item-type="item">
        {{
          item.type === "Mobile"
            ? "iOS/iPadOS"
            : item.type === "Computer"
            ? "macOS"
            : "Unknown"
        }}
      </template>
      <template #item-targetVersion="item">
        <v-select
          v-model="item.targetVersion"
          chips
          :items="
            item.type === 'Mobile'
              ? iOSVersions
              : item.type === 'Computer'
              ? macOSVersions
              : []
          "
          variant="outlined"
          density="compact"
          :readonly="readOnlyStates.includes(item.status)"
          @update:model-value="(value) => updateTargetVersion(item, value)"
          class="d-flex align-center"
        ></v-select>
      </template>
      <template #expand="item">
        <strong>Deployment Errors:</strong>
        {{
          item.errorReasons.length > 0 ? item.errorReasons.join(", ") : "N/A"
        }}
      </template>
      <template #item-cancelPlan="item">
        <v-icon
          v-if="!statuses.includes(item.planStatus)"
          icon="mdi-cancel"
          size="x-large"
          color="red"
          @click="cancelPlans('Single', item)"
        ></v-icon>
        <v-icon v-else icon="mdi-cancel" size="x-large" color="grey"></v-icon>
      </template>
      <template #item-updatePending="item">
        <v-icon
          v-if="item.updatePending === true"
          icon="mdi-cancel"
          size="x-large"
          color="green"
        ></v-icon>
        <v-icon v-else icon="mdi-cancel" size="x-large" color="grey"></v-icon>
      </template>
      <template #item-start="item">
        <VueDatePicker
          v-model="item.start"
          :teleport="true"
          :format="dateFormat"
          :readonly="readOnlyStates.includes(item.status)"
          model-type="format"
          @update:model-value="setDatePicker(item, 'Start')"
          style="width: 220px"
        />
      </template>
      <template #item-end="item">
        <VueDatePicker
          v-model="item.end"
          :teleport="true"
          :format="dateFormat"
          :readonly="readOnlyStates.includes(item.status)"
          model-type="format"
          @update:model-value="setDatePicker(item, 'End')"
          style="width: 220px"
        />
      </template>
    </EasyDataTable>
    <v-spacer class="mb-4"></v-spacer>
    <v-row v-if="appBar.selected && mdmServer.$state.type === 'Jamf'">
      <v-col cols="auto">
        <v-btn color="primary" class="mr-4" @click="addGroup()"
          >Add Group</v-btn
        >
        <v-btn
          v-if="updateGroups.length > 0"
          color="primary"
          @click="removeGroup()"
          >Remove Group</v-btn
        >
      </v-col>
      <v-col cols="auto" class="ml-auto">
        <v-btn
          class="mr-4"
          color="indigo-darken-3"
          href="/Update Automation Guide v1.1.pdf"
          target="_blank"
        >
          Download Guide
        </v-btn>
        <v-btn
          v-if="updateGroups.length > 0"
          class="mr-4"
          color="primary"
          @click="cancelPlans('All')"
          >Cancel All Running Plans</v-btn
        >
        <v-btn color="primary" @click="saveChanges()">Save Changes</v-btn>
      </v-col>
    </v-row>
    <v-dialog v-model="showAddList" style="height: 1000px; width: 800px">
      <template v-slot:default="{ isActive }">
        <v-card class="d-flex flex-column">
          <v-card-title> Select Update Smart Group </v-card-title>
          <v-divider class="mt-3"></v-divider>
          <v-card-text>
            Please note that any group you select will have the 'OS Version'
            (Mobile) or 'Operating System Version' (Computer) criteria added to
            it upon saving. Please use a dedicated group for scoping updates.
            Download the guide for further details.
          </v-card-text>
          <v-divider class="mt-3"></v-divider>
          <div style="overflow-y: auto; max-height: 500px; padding: 16px">
            <v-radio-group v-model="selectedGroup">
              <v-table density="compact">
                <thead style="background-color: lightgrey">
                  <tr>
                    <th>Select</th>
                    <th>Group Name</th>
                    <th>Group Type</th>
                    <th>Group ID</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="group in newGroups" :key="group.id">
                    <td>
                      <v-radio :value="group" />
                    </td>
                    <td>{{ group.name }}</td>
                    <td>{{ group.type || "Unknown" }}</td>
                    <td>{{ group.id }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-radio-group>
          </div>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn text="Close" @click="isActive.value = false"></v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="surface-variant"
              text="Add"
              variant="flat"
              @click="addSelectedGroup(selectedGroup)"
            ></v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
    <v-dialog v-model="showRemoveList" style="height: 1000px; width: 800px">
      <template v-slot:default="{ isActive }">
        <v-card class="d-flex flex-column">
          <v-card-title>Select Group to Remove</v-card-title>
          <v-divider class="mt-3"></v-divider>
          <v-card-text>
            Please note that removing a group will cancel any in-progress update
            plan.
          </v-card-text>
          <v-divider class="mt-3"></v-divider>

          <div style="overflow-y: auto; max-height: 500px; padding: 16px">
            <v-radio-group v-model="selectedRemoveGroup">
              <v-table density="compact">
                <thead style="background-color: lightgrey">
                  <tr>
                    <th>Select</th>
                    <th>Group Name</th>
                    <th>Group Type</th>
                    <th>Group ID</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="group in updateGroups" :key="group.id">
                    <td>
                      <v-radio :value="group" />
                    </td>
                    <td>{{ group.name }}</td>
                    <td>{{ group.type || "Unknown" }}</td>
                    <td>{{ group.id }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-radio-group>
          </div>

          <v-divider></v-divider>
          <v-card-actions>
            <v-btn text="Close" @click="isActive.value = false"></v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="surface-variant"
              text="Remove All"
              variant="flat"
              @click="removeGroup('All')"
            ></v-btn>
            <v-btn
              color="surface-variant"
              text="Remove"
              variant="flat"
              @click="removeGroup('Single', selectedRemoveGroup)"
            ></v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-col>
  <v-dialog v-model="cancelDialogBox" width="500">
    <v-card>
      <v-card-title class="headline">Warning</v-card-title>
      <v-card-text>{{ cancelDialogBoxMessage }}</v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text="Proceed"
          @click="cancelPlans('API', objectsToCancel)"
        ></v-btn>
        <v-btn
          color="primary"
          text="Cancel"
          @click="cancelPlans('Cancel')"
        ></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="loading" persistent width="auto">
    <v-card color="primary">
      <v-card-text>
        Loading, please wait...
        <v-progress-linear indeterminate></v-progress-linear>
      </v-card-text>
    </v-card>
  </v-dialog>
  <v-dialog v-model="updating" persistent width="auto">
    <v-card color="primary">
      <v-card-text>
        Updating, please wait...
        <v-progress-linear indeterminate></v-progress-linear>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
